import { useState } from "react";
import { ActionIcon, Button, Card, Group, Stack, Text } from "@mantine/core";
import { IconExternalLink, IconSettings } from "@tabler/icons-react";
import dayjs from "dayjs";

import { useCloseStation, useUpdateStation } from "../../api/checkInStation";
import { useConfirm } from "../../hooks/useConfirm";
import StationSettingsModal from "./StationSettingsModal";

const StationCard = ({ station, onOpen }) => {
  const closeStation = useCloseStation(station.id);
  const updateStation = useUpdateStation(station.id);
  const confirm = useConfirm();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const handleSaveSettings = (settings) =>
    updateStation.mutate(settings, {
      onSuccess: () => setIsSettingsOpen(false),
    });

  const handleClose = () =>
    confirm({
      title: "Close station?",
      message:
        "This station will be closed and can no longer be used for check-in.",
      confirmLabel: "Close station",
      confirmColor: "red",
      onConfirm: () => closeStation.mutate({}),
    });

  const createdBy = `${station.createdByFirstName} ${station.createdByLastName}`;
  const createdAt = dayjs(station.createdAt).format("DD.MM.YYYY. [at] HH:mm");

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          {station.isScannerEnabled ? (
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Scan source
              </Text>
              <Text fz={32} fw={700}>
                USB scanner #{station.id}
              </Text>
            </Stack>
          ) : (
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Pairing code
              </Text>
              <Text fz={32} fw={700} style={{ letterSpacing: 4 }}>
                {station.code}
              </Text>
            </Stack>
          )}
          <ActionIcon
            variant="default"
            size="lg"
            radius="sm"
            onClick={() => setIsSettingsOpen(true)}
            aria-label="Station settings"
          >
            <IconSettings size={18} />
          </ActionIcon>
        </Group>

        <Text size="sm" c="dimmed">
          Created by {createdBy} - {createdAt}
        </Text>

        <Group justify="flex-start" gap="sm">
          <Button
            variant="light"
            color="red"
            onClick={handleClose}
            loading={closeStation.isPending}
          >
            Close
          </Button>
          <Button
            variant="light"
            leftSection={<IconExternalLink size={16} />}
            onClick={() => onOpen(station)}
          >
            Open
          </Button>
        </Group>
      </Stack>

      {isSettingsOpen && (
        <StationSettingsModal
          initialPrintingEnabled={!!station.isPrintingEnabled}
          initialAutoPrintEnabled={!!station.isAutoPrintEnabled}
          initialScannerEnabled={!!station.isScannerEnabled}
          isLoading={updateStation.isPending}
          onConfirm={handleSaveSettings}
          onClose={() => setIsSettingsOpen(false)}
        />
      )}
    </Card>
  );
};

export default StationCard;
